import { useEffect, useMemo, useState } from 'react';
import { Coffee, Eye, Timer } from 'lucide-react';

const TOTAL_SECONDS = 60 * 60;
const BREAK_AT = 30 * 60;
const BREAK_SECONDS = 5 * 60;

const fmt = (s) => {
  const sec = Math.max(0, Math.round(s));
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
};

/**
 * StudyTimer — the 60-minute chapter clock.
 *
 * Three promises to the student: the clock is encouragement, never a threat;
 * eyes get a real 5-minute break after 30 minutes; and going over 60 minutes is
 * reported kindly, because a slower chapter is still a finished chapter.
 */
export function StudyTimer({ sessionId, paused = false, onBreakChange }) {
  const storeKey = `gv_session_clock_${sessionId}`;
  const [startedAt] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(storeKey));
      if (saved) return saved;
      window.localStorage.setItem(storeKey, String(Date.now()));
    } catch { /* private mode */ }
    return Date.now();
  });
  const [elapsed, setElapsed] = useState(0);
  const [breakUntil, setBreakUntil] = useState(0);
  const [breakTaken, setBreakTaken] = useState(false);

  useEffect(() => {
    const tick = () => setElapsed(Math.round((Date.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  useEffect(() => {
    if (paused || breakTaken || elapsed < BREAK_AT) return;
    setBreakTaken(true);
    setBreakUntil(Date.now() + BREAK_SECONDS * 1000);
  }, [elapsed, paused, breakTaken]);

  const onBreak = breakUntil > Date.now();
  useEffect(() => { onBreakChange?.(onBreak); }, [onBreak, onBreakChange]);

  const overtime = elapsed > TOTAL_SECONDS;
  const left = TOTAL_SECONDS - elapsed;
  const pct = Math.min(100, Math.round((elapsed / TOTAL_SECONDS) * 100));

  const tone = useMemo(() => {
    if (overtime) return 'bg-amber-50 text-amber-800 ring-amber-200';
    if (left < 10 * 60) return 'bg-rose-50 text-rose-700 ring-rose-200';
    return 'bg-slate-900 text-white ring-slate-900/10';
  }, [overtime, left]);

  return (
    <div className="space-y-2" data-testid="study-timer">
      <div className={`rounded-2xl ring-1 px-3 py-2.5 ${tone}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 font-heading text-[10px] uppercase tracking-widest opacity-80">
            <Timer className="w-3 h-3" /> {overtime ? 'Extra time' : 'Chapter clock'}
          </span>
          <span className="font-heading text-lg tabular-nums" data-testid="study-timer-value">
            {overtime ? `+${fmt(elapsed - TOTAL_SECONDS)}` : fmt(left)}
          </span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full bg-current/70 transition-all duration-1000" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1.5 font-body text-[11px] opacity-85" data-testid="study-timer-note">
          {overtime
            ? "You're taking a little longer today — that's fine, finishing matters more than the clock."
            : left < 10 * 60
              ? 'Final stretch — finish the step you are on and the chapter is yours.'
              : 'Sixty minutes is all this chapter needs. I\'ll call a 5-minute eye break at the halfway mark.'}
        </p>
      </div>

      {onBreak && (
        <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5" data-testid="study-break-banner">
          <p className="font-heading text-[12px] text-emerald-900 flex items-center gap-1.5">
            <Coffee className="w-3.5 h-3.5" /> Break time · {fmt((breakUntil - Date.now()) / 1000)}
          </p>
          <p className="font-body text-[11px] text-emerald-800/90 mt-1 flex items-start gap-1.5">
            <Eye className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            Look away from the screen, walk a bit, drink some water. Your eyes have earned this — I'll wait.
          </p>
          <button data-testid="study-break-skip" onClick={() => setBreakUntil(0)}
            className="mt-1.5 text-[11px] font-heading text-emerald-700 hover:underline">
            I'm back early
          </button>
        </div>
      )}
    </div>
  );
}

export default StudyTimer;
